/**
 * Shared picking-list filter helpers.
 *
 * Kept here (module scope, no React) so both the list view (ModernPickingList)
 * and the "Nästa plock" dispatcher (ModernPickingApp) use one source of truth.
 */

// Device-local key for the "due today or overdue" availability filter
// (persisted per tablet — no DB / SettingDefinition involved).
export const DELIVERY_TODAY_FILTER_KEY = 'bizclick_picking_delivery_today_filter';

/**
 * True if the picking list is due today or earlier (overdue).
 * Picks without a delivery date are treated as "not due today" and are hidden
 * while the filter is on. Comparison is by calendar day in local time, so a
 * delivery timestamp anywhere within today still counts as today.
 *
 * lookaheadDays (setting picking.dueDateLookaheadDays, default 0) utvider
 * vinduet X dager frem: 0 = kun i dag/forfalt, 2 = t.o.m. i overmorgen.
 * Samme vindu brukes av dashbord-telleren (OperatorDashboardService) slik at
 * plukk-kortet og lista alltid teller samme utvalg.
 */
export const isDueTodayOrEarlier = (deliveryDate?: string | null, lookaheadDays: number = 0): boolean => {
  if (!deliveryDate) return false;
  const d = new Date(deliveryDate);
  if (isNaN(d.getTime())) return false;
  const today = new Date();
  const deliveryDay = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const limitDay = new Date(today.getFullYear(), today.getMonth(), today.getDate() + lookaheadDays).getTime();
  return deliveryDay <= limitDay;
};

export type DueStatus = 'overdue' | 'today' | 'future';

/**
 * Forfallsstatus for leveringsdatoen på plukkortet, per kalenderdag i lokal tid
 * (samme sammenligning som isDueTodayOrEarlier). null når datoen mangler/er ugyldig.
 */
export const getDueStatus = (deliveryDate?: string | null): DueStatus | null => {
  if (!deliveryDate) return null;
  const d = new Date(deliveryDate);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  const deliveryDay = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  if (deliveryDay < todayDay) return 'overdue';
  if (deliveryDay === todayDay) return 'today';
  return 'future';
};

/** Leveringsdato som dd.mm.åå (f.eks. 30.10.26) — datoene lagres uten relevant klokkeslett. */
export const formatDeliveryDate = (deliveryDate: string): string => {
  const d = new Date(deliveryDate);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear() % 100).padStart(2, '0');
  return `${dd}.${mm}.${yy}`;
};
